/*
 * Copyright (c) 2020. FINN Sp. z o.o.
 */
@file:UseContextualSerialization(LocalDate::class)

package pl.finn.forms.tw1

import io.kvision.types.LocalDate
import kotlinx.serialization.Serializable
import kotlinx.serialization.UseContextualSerialization
import pl.finn.forms.Zalacznik
import kotlin.random.Random

@Serializable
data class DocumentTreeNode(
  val id: String = "finn_" + getRandomString(),
  var value: String? = null
)

@Serializable
data class Tw1Form(
  val namespaceURI: String? = null,
  val kodUrzedu: DocumentTreeNode = DocumentTreeNode(),
  val uuid: DocumentTreeNode = DocumentTreeNode(),
  val nazwaPliku: DocumentTreeNode = DocumentTreeNode(),
  val data: DocumentTreeNode = DocumentTreeNode(),
  val kodFormularza: DocumentTreeNode = DocumentTreeNode(),
  val kodSystemowy: DocumentTreeNode = DocumentTreeNode(),
  val wersjaSchemy: DocumentTreeNode = DocumentTreeNode(),
  val sygnatura: DocumentTreeNode = DocumentTreeNode(),
  val dataWystawienia: DocumentTreeNode = DocumentTreeNode(),
  val rodzajDokumentu: DocumentTreeNode = DocumentTreeNode(),
  val dtw: DocumentTreeNode = DocumentTreeNode(value = "false"),
  val pierwotnyTW: DocumentTreeNode = DocumentTreeNode(),
  val nazwaUrzedu: DocumentTreeNode = DocumentTreeNode(),
  val adresOrganuEgzekucyjnego: AdresPolski = AdresPolski(),
  val dataNadaniaKlauzuli: DocumentTreeNode = DocumentTreeNode(),
  val numerPorzadkowy: DocumentTreeNode = DocumentTreeNode(),
  val celWydania: DocumentTreeNode = DocumentTreeNode(),
  val dataWydaniaDalszegoTW: DocumentTreeNode = DocumentTreeNode(),
  val dotyczyDokumentu: DocumentTreeNode = DocumentTreeNode(),
  val kosztyEgzekucyjne: DocumentTreeNode = DocumentTreeNode(),
  val adnotacja: DocumentTreeNode = DocumentTreeNode(value = "false"),
  val dataWystawieniaPonownegoTW: DocumentTreeNode = DocumentTreeNode(),
  val nazwaWierzyciela: DocumentTreeNode = DocumentTreeNode(),
  val sygnaturaPostanowienia: DocumentTreeNode = DocumentTreeNode(),
  val dataWydaniaPostanowienia: DocumentTreeNode = DocumentTreeNode(),
  val rodzajUtraconegoDokumentu: DocumentTreeNode = DocumentTreeNode(),
  val zobowiazany: DocumentTreeNode = DocumentTreeNode(),
  val zobowiazanyOsobaFizyczna: OsobaFizyczna = OsobaFizyczna(),
  val zobowiazanyOsobaFizycznaZobowiazanyPierwotny: DocumentTreeNode = DocumentTreeNode(),
  val zobowiazanyOsobaFizycznaZobowiazanyPierwotnyData: ArrayList<ZobowiazanyPierwotny> = arrayListOf(ZobowiazanyPierwotny()),
  val zobowiazanyOsobaNiefizyczna: OsobaNiefizyczna = OsobaNiefizyczna(),
  val zobowiazanyOsobaNiefizycznaWspolnicy: DocumentTreeNode = DocumentTreeNode(),
  val zobowiazanyOsobaNiefizycznaWspolnicyData: ArrayList<Wspolnik> = arrayListOf(Wspolnik()),
  val zobowiazanyOsobaNiefizycznaZobowiazanyPierwotny: DocumentTreeNode = DocumentTreeNode(),
  val zobowiazanyOsobaNiefizycznaZobowiazanyPierwotnyData: ArrayList<ZobowiazanyPierwotny> = arrayListOf(ZobowiazanyPierwotny()),
  val zobowiazanyMalzonkowie: ArrayList<OsobaFizyczna> = arrayListOf(OsobaFizyczna(), OsobaFizyczna()),
  val zobowiazanyMalzonekZobowiazanyPierwotny: DocumentTreeNode = DocumentTreeNode(),
  val zobowiazanyMalzonekZobowiazanyPierwotnyData: ArrayList<ZobowiazanyPierwotny> = arrayListOf(ZobowiazanyPierwotny()),
  val naleznoscAktNormatywny: DocumentTreeNode = DocumentTreeNode(),
  val naleznoscRodzajNaleznosci: DocumentTreeNode = DocumentTreeNode(),
  val naleznoscRodzajNaleznosciKodPodstawyPrawnej: DocumentTreeNode = DocumentTreeNode(),
  val naleznoscRodzajNaleznosciRodzaj: DocumentTreeNode = DocumentTreeNode(),
  val naleznoscObowiazek: DocumentTreeNode = DocumentTreeNode(),
  val naleznoscObowiazekZMocyPrawa: DocumentTreeNode = DocumentTreeNode(),
  val naleznoscObowiazekInnyDokument: DocumentTreeNode = DocumentTreeNode(),
  val naleznoscObowiazekOrzeczenie: DocumentTreeNode = DocumentTreeNode(),
  val naleznoscOrzeczenieDataWydania: DocumentTreeNode = DocumentTreeNode(),
  val naleznoscZabezpieczenia: NaleznoscZabezpieczenia = NaleznoscZabezpieczenia(),
  val naleznoscDoreczenia: DocumentTreeNode = DocumentTreeNode(),
  val naleznoscDoreczeniaBrakUpomnienia: DocumentTreeNode = DocumentTreeNode(),
  val naleznoscDoreczeniaKosztyUpomnienia: DocumentTreeNode = DocumentTreeNode(),
  val naleznoscDoreczeniaDataDoreczenia: ArrayList<DocumentTreeNode> = arrayListOf(DocumentTreeNode()),
  val naleznoscOgranOdpowiedzialny: Podmiot = Podmiot(),
  val naleznoscOgranUdzielajacyInformacji: DocumentTreeNode = DocumentTreeNode(value = "false"),
  val naleznoscOgranUdzielajacyInformacjiData: Podmiot = Podmiot(),
  val zobowiazanie: DocumentTreeNode = DocumentTreeNode(),
  val zobowiazanieBrakOdsetekPozycja: ArrayList<Pozycja> = arrayListOf(Pozycja()),
  val zobowiazanieOdsetkiRodzaj: DocumentTreeNode = DocumentTreeNode(),
  val zobowiazanieOdsetkiPozycja: ArrayList<Pozycja> = arrayListOf(Pozycja()),
  val zobowiazanieOdsetki: DocumentTreeNode = DocumentTreeNode(),
  val zobowiazanieOdsetkiStawka: DocumentTreeNode = DocumentTreeNode(),
  val zobowiazanieOdsetkiPodwyzszonaStawka: DocumentTreeNode = DocumentTreeNode(),
  val innaPodstawaPrawna: DocumentTreeNode = DocumentTreeNode(),
  val odpowiedzialnoscObejmujeMajatekWspolny: DocumentTreeNode = DocumentTreeNode(),
  val obowiazekScisleZwiazanyZeZobowiazanym: DocumentTreeNode = DocumentTreeNode(),
  val wierzycielKodWierzyciela: DocumentTreeNode = DocumentTreeNode(),
  val wierzycielNazwa: DocumentTreeNode = DocumentTreeNode(),
  val wierzycielAdres: AdresPolski = AdresPolski(),
  val wierzycielNip: DocumentTreeNode = DocumentTreeNode(),
  val wierzycielRegon: DocumentTreeNode = DocumentTreeNode(),
  val wierzycielNrTelefonu: DocumentTreeNode = DocumentTreeNode(),
  val wierzycielEmail: DocumentTreeNode = DocumentTreeNode(),
  val wierzycielSkrytkaEPuap: DocumentTreeNode = DocumentTreeNode(),
  val beneficjentNazwa: DocumentTreeNode = DocumentTreeNode(),
  val beneficjentAdres: AdresPolski = AdresPolski(),
  val beneficjentNrTelefonu: DocumentTreeNode = DocumentTreeNode(),
  val beneficjentEmail: DocumentTreeNode = DocumentTreeNode(),
  val beneficjentNrRachunkuPl: DocumentTreeNode = DocumentTreeNode(),
  val wniosekOWszczecie: DocumentTreeNode = DocumentTreeNode(),
  val osobaUpowaznionaImie: DocumentTreeNode = DocumentTreeNode(),
  val osobaUpowaznionaNazwisko: DocumentTreeNode = DocumentTreeNode(),
  val osobaUpowaznionaStanowisko: DocumentTreeNode = DocumentTreeNode(),
  val zakresInfomacji: DocumentTreeNode = DocumentTreeNode(),
  val skladnikMajatku: ArrayList<DocumentTreeNode> = arrayListOf(DocumentTreeNode()),
  val adresDluznikaZobowiazanegoAdres: DocumentTreeNode = DocumentTreeNode(),
  val adresDluznikaZobowiazanegoAdresData: Adres = Adres(),
  val adresDluznikaZobowiazanegoNrTelefonu: DocumentTreeNode = DocumentTreeNode(),
  val dataNumerWnioskuDataWyslania: DocumentTreeNode = DocumentTreeNode(),
  val dataNumerWnioskuNrRefWniosku: DocumentTreeNode = DocumentTreeNode(),
  val postanowienieUmorzeniaDataWydania: DocumentTreeNode = DocumentTreeNode(),
  val postanowienieUmorzeniaNrPostanowienia: DocumentTreeNode = DocumentTreeNode(),
  val postanowienieUmorzeniaTrescInformacji: DocumentTreeNode = DocumentTreeNode(),
  val reprezentantTypPrzedstawiciela: DocumentTreeNode = DocumentTreeNode(),
  val reprezentantImie: DocumentTreeNode = DocumentTreeNode(),
  val reprezentantNazwisko: DocumentTreeNode = DocumentTreeNode(),
  val reprezentantAdres: DocumentTreeNode = DocumentTreeNode(),
  val reprezentantAdresData: Adres = Adres(),
  val reprezentantNrTelefonu: DocumentTreeNode = DocumentTreeNode(),
  val reprezentantEmail: DocumentTreeNode = DocumentTreeNode(),
  val reprezentantSkrytkaEPuap: DocumentTreeNode = DocumentTreeNode(),
  val zarzadcaWSpadkuImie: DocumentTreeNode = DocumentTreeNode(),
  val zarzadcaWSpadkuNazwisko: DocumentTreeNode = DocumentTreeNode(),
  val zarzadcaWSpadkuAdres: DocumentTreeNode = DocumentTreeNode(),
  val zarzadcaWSpadkuAdresData: Adres = Adres(),
  val zarzadcaWSpadkuNrTelefonu: DocumentTreeNode = DocumentTreeNode(),
  val zarzadcaWSpadkuEmail: DocumentTreeNode = DocumentTreeNode(),
  val zarzadcaWSpadkuSkrytkaEPuap: DocumentTreeNode = DocumentTreeNode(),
  val daneZmarlegoPrzedsiebiorcyImie: DocumentTreeNode = DocumentTreeNode(),
  val daneZmarlegoPrzedsiebiorcyNazwisko: DocumentTreeNode = DocumentTreeNode(),
  val daneZmarlegoPrzedsiebiorcyPesel: DocumentTreeNode = DocumentTreeNode(),
  val majatekZobowiazanegoNaTerenieOE: ArrayList<DocumentTreeNode> = arrayListOf(DocumentTreeNode()),
  val innaOkolicznosc: ArrayList<DocumentTreeNode> = arrayListOf(DocumentTreeNode()),
)

@Serializable
data class OsobaFizyczna(
  val imie: DocumentTreeNode = DocumentTreeNode(),
  val nazwisko: DocumentTreeNode = DocumentTreeNode(),
  val admId: DocumentTreeNode = DocumentTreeNode(),
  val nip1: DocumentTreeNode = DocumentTreeNode(),
  val pesel1: DocumentTreeNode = DocumentTreeNode(),
  val pesel2: DocumentTreeNode = DocumentTreeNode(),
  val nip2: DocumentTreeNode = DocumentTreeNode(),
  val oswiadczenieWierzyciela: DocumentTreeNode = DocumentTreeNode(),
  val adres: DocumentTreeNode = DocumentTreeNode(),
  val adresData: Adres = Adres(),
  val nrTelefonu: DocumentTreeNode = DocumentTreeNode(),
  val email: DocumentTreeNode = DocumentTreeNode(),
  val dataUrodzenia: DocumentTreeNode = DocumentTreeNode(),
  val odpowiedzialnosc: DocumentTreeNode = DocumentTreeNode(),
)

@Serializable
data class OsobaNiefizyczna(
  val wSpadku: DocumentTreeNode = DocumentTreeNode(),
  val nazwa: DocumentTreeNode = DocumentTreeNode(),
  val admId: DocumentTreeNode = DocumentTreeNode(),
  val regon1: DocumentTreeNode = DocumentTreeNode(),
  val nip1: DocumentTreeNode = DocumentTreeNode(),
  val nip2: DocumentTreeNode = DocumentTreeNode(),
  val regon2: DocumentTreeNode = DocumentTreeNode(),
  val oswiadczenie: DocumentTreeNode = DocumentTreeNode(),
  val adres: DocumentTreeNode = DocumentTreeNode(),
  val adresData: Adres = Adres(),
  val nrTelefonu: DocumentTreeNode = DocumentTreeNode(),
  val email: DocumentTreeNode = DocumentTreeNode(),
  val odpowiedzialnosc: DocumentTreeNode = DocumentTreeNode(),
  )

@Serializable
data class Podmiot(
  val nazwa: DocumentTreeNode = DocumentTreeNode(),
  val adres: DocumentTreeNode = DocumentTreeNode(),
  val adresData: Adres = Adres(),
  val nrTelefonu: DocumentTreeNode = DocumentTreeNode(),
  val email: DocumentTreeNode = DocumentTreeNode(),
  val skrytkaEPuap: DocumentTreeNode = DocumentTreeNode(),
)

@Serializable
data class Wspolnik(
  val partner: DocumentTreeNode = DocumentTreeNode(),
  val osobaFizyczna: OsobaFizyczna = OsobaFizyczna(),
  val osobaNiefizyczna: OsobaNiefizyczna = OsobaNiefizyczna(),
)

@Serializable
data class Adres(
  val adresPolski: AdresPolski = AdresPolski(),
  val adresZagranicznyKodKraju: DocumentTreeNode = DocumentTreeNode(),
  val adresZagranicznyKodPocztowy: DocumentTreeNode = DocumentTreeNode(),
  val adresZagranicznyMiejscowosc: DocumentTreeNode = DocumentTreeNode(),
  val adresZagranicznyUlica: DocumentTreeNode = DocumentTreeNode(),
  val adresZagranicznyNrDomu: DocumentTreeNode = DocumentTreeNode(),
  val adresZagranicznyNrLokalu: DocumentTreeNode = DocumentTreeNode(),
)

@Serializable
data class AdresPolski(
  val kodKraju: DocumentTreeNode = DocumentTreeNode(),
  val wojewodztwo: DocumentTreeNode = DocumentTreeNode(),
  val powiat: DocumentTreeNode = DocumentTreeNode(),
  val gmina: DocumentTreeNode = DocumentTreeNode(),
  val miejscowosc: DocumentTreeNode = DocumentTreeNode(),
  val ulica: DocumentTreeNode = DocumentTreeNode(),
  val nrDomu: DocumentTreeNode = DocumentTreeNode(),
  val nrLokalu: DocumentTreeNode = DocumentTreeNode(),
  val kodPocztowy: DocumentTreeNode = DocumentTreeNode(),
)

@Serializable
data class ZobowiazanyPierwotny(
  val obligated: DocumentTreeNode = DocumentTreeNode(),
  val osobaFizyczna: OsobaFizyczna = OsobaFizyczna(),
  val osobaNiefizyczna: OsobaNiefizyczna = OsobaNiefizyczna(),
)

@Serializable
data class Pozycja(
  val kwota: DocumentTreeNode = DocumentTreeNode(),
  val odsetkiDniaOd: DocumentTreeNode = DocumentTreeNode(),
  val kwotaOdsetek: DocumentTreeNode = DocumentTreeNode(),
  val dochodzicDniaDo: DocumentTreeNode = DocumentTreeNode(),
  val okresDniaOd: DocumentTreeNode = DocumentTreeNode(),
  val okresDniaDo: DocumentTreeNode = DocumentTreeNode(),
  val podstawaPrawnaPierwszenstwa: DocumentTreeNode = DocumentTreeNode(),
)

@Serializable
data class Tw1FormData(
  val form: Tw1Form,
  val zalaczniki: List<Zalacznik>,
  val url: String,
  val token: String?
)

@Serializable
data class NaleznoscZabezpieczenia(
  val posiadaZabezpieczenie: DocumentTreeNode = DocumentTreeNode(value = "false"),
  val hipotekaZastaw: DocumentTreeNode = DocumentTreeNode(),
  val dataPowstania: DocumentTreeNode = DocumentTreeNode()
)

fun getRandomString(): String {
  val allowedChars = ('a'..'z') + ('A'..'Z')
  return (1..8)
    .map { allowedChars.random() }
    .joinToString("")
}


